import { isSynthTrack, playlistM3u, trackDuration } from '@/config/media';
import { trackFor, tracksIn } from './media';
import { dirname, list } from './resolve';
import type { FsFolder, FsNode } from './types';

/**
 * How big a file is, and when it was last written.
 *
 * Three views of the disk ask these questions and every one of them has to
 * give the same answer: Explorer's Details columns, Arrange Icons By out on
 * the desktop, and the Properties sheet. Two views of one folder disagreeing
 * about which file is bigger would be worse than either answer alone, so the
 * arithmetic lives here once rather than in each of them.
 *
 * Where the disk knows the real number, this returns the real number. An mp3
 * is exactly as big as a constant bitrate running for its duration makes it,
 * and a `.url` or an `.m3u` is exactly as long as the text `readText()` writes
 * out for it -- the same text, so the file Notepad shows you and the size
 * Explorer reports are the same object. Everything else has no bytes to count
 * and falls back to a stable hash of the name: a Size column that changed on
 * every render would be worse than a derived one.
 */

/** FNV-1a. Any stable hash will do; this one is short and has no collisions
    worth worrying about across a few dozen names. */
export function hashOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * NTFS's default allocation unit. A file occupies whole clusters, which is the
 * entire reason Size on disk is a separate line from Size, and why a one-byte
 * file takes four kilobytes.
 */
export const CLUSTER = 4096;

const HOUR = 3_600_000;

/** Bytes, counted the way a file counts them rather than the way JS counts
    string length: `readText()` writes CRLF, and a track title need not be
    ASCII. */
function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * Size in bytes, or `null` for a folder, which has no size of its own -- only
 * the size of what is under it. See `folderTotals`.
 */
export function sizeOf(node: FsNode): number | null {
  if (node.type === 'folder') return null;

  switch (node.data.kind) {
    case 'audio': {
      const track = trackFor(node);
      if (!track) return 0;
      // A piece the synth performs is note data, and a few hundred bytes of it.
      if (isSynthTrack(track)) return utf8Bytes(JSON.stringify(track.notes));
      // The rest are constant-rate, which is what makes this exact rather than
      // an estimate: kilobits per second, for this many seconds, in bytes.
      return Math.round(((track.bitrate ?? 128) * 1000 * trackDuration(track)) / 8);
    }

    // Both of these are generated on demand by `readText()` in `useFs`, out of
    // the same source. Kept in step with it deliberately: the size is the
    // length of the file you would actually be shown.
    case 'url':
      return utf8Bytes(`[InternetShortcut]\r\nURL=${node.data.href}\r\n`);
    case 'playlist':
      return utf8Bytes(playlistM3u(tracksIn(dirname(node.path))));

    default:
      // Whole kilobytes, so `sizeKb` below reproduces this number exactly.
      return (1 + (hashOf(node.name) % 96)) * 1024;
  }
}

/** What Explorer's Size column shows: whole kilobytes, rounded up. */
export function sizeKb(node: FsNode): number | null {
  const bytes = sizeOf(node);
  return bytes === null ? null : Math.ceil(bytes / 1024);
}

/** Space actually occupied: whole clusters. */
export function sizeOnDisk(bytes: number): number {
  return Math.ceil(bytes / CLUSTER) * CLUSTER;
}

/* --------------------------------------------------------------------------
   Timestamps

   A plausible XP-era stamp, spread over a few months and deterministic. The
   three are derived from one another rather than independently, because a file
   created after it was last written is the kind of detail that is only ever
   noticed when it is wrong.
   -------------------------------------------------------------------------- */

const EPOCH = Date.UTC(2004, 7, 24, 9, 12);

export function modifiedAt(node: FsNode): Date {
  return new Date(EPOCH + (hashOf(node.path) % 4200) * HOUR);
}

export function createdAt(node: FsNode): Date {
  return new Date(modifiedAt(node).getTime() - (hashOf(`${node.path} created`) % 2000) * HOUR);
}

export function accessedAt(node: FsNode): Date {
  return new Date(modifiedAt(node).getTime() + (hashOf(`${node.path} accessed`) % 400) * HOUR);
}

/* --------------------------------------------------------------------------
   Folders
   -------------------------------------------------------------------------- */

export interface FolderTotals {
  bytes: number;
  /** The same bytes rounded up to whole clusters, file by file. */
  onDisk: number;
  files: number;
  folders: number;
}

/**
 * What a folder contains, all the way down.
 *
 * Walks by `list()` rather than by `children`, so a folder in the Recycle Bin
 * stops counting towards the total the moment it goes in -- which is what makes
 * the number on the sheet agree with the folder you are looking at.
 */
export function folderTotals(folder: FsFolder): FolderTotals {
  let bytes = 0;
  let onDisk = 0;
  let files = 0;
  let folders = 0;

  const walk = (path: string): void => {
    for (const child of list(path)) {
      if (child.type === 'folder') {
        folders += 1;
        walk(child.path);
        continue;
      }
      const size = sizeOf(child) ?? 0;
      files += 1;
      bytes += size;
      onDisk += sizeOnDisk(size);
    }
  };

  walk(folder.path);
  return { bytes, onDisk, files, folders };
}

/* --------------------------------------------------------------------------
   Formatting
   -------------------------------------------------------------------------- */

const UNITS = ['KB', 'MB', 'GB'] as const;

/**
 * Three significant figures, which is what `StrFormatByteSize` did and why the
 * shell showed `1.20 KB`, `12.3 KB` and `123 KB` rather than a fixed number of
 * decimal places.
 */
function threeSig(value: number): string {
  if (value >= 100) return Math.round(value).toString();
  if (value >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

/** Thousands separators, pinned to one locale so the number never moves. */
export function groupDigits(n: number): string {
  return n.toLocaleString('en-US');
}

/** The property sheet's Size line: `12.3 KB (12,634 bytes)`. */
export function formatSize(bytes: number): string {
  const exact = groupDigits(bytes);
  if (bytes < 1024) return `${exact} bytes`;

  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${threeSig(value)} ${UNITS[unit]} (${exact} bytes)`;
}
